/**
 * The views of a machine's page and how they are grouped. The machine's own views come first, in the order the phone
 * app uses too (Overview, Sessions, Terminal or a node's Console, Apps, Activity, Settings); then AI Workbench's own
 * groups, with its titles, blurbs and icons (webview-ui AppShell.tsx), and each view's tone as the app has it; the
 * screen last, as one quiet item. `needs` names what a view reads from the machine besides its own polls, so a view
 * that is not open asks for nothing.
 */

import {
  Book,
  BranchesRight,
  Clock,
  Comments,
  Cubes3,
  Display,
  Folder,
  Gear,
  GraphNode,
  LayoutCells,
  House,
  ListCheck,
  Magnifier,
  NodesRight,
  Persons,
  Pulse,
  Rocket,
  Route,
  Eye,
  Terminal,
} from '@gravity-ui/icons';
import type { ReactNode } from 'react';

import type { ArtTone } from '../../components/ui/Art.tsx';
import type { Workbench, WorkbenchNeed } from '../useWorkbench.ts';
import { ActivityView } from './ActivityView.tsx';
import { AppOnlyView } from './AppOnlyView.tsx';
import { AppsView } from './AppsView.tsx';
import { ConsoleView } from './ConsoleView.tsx';
import { GitView } from './GitView.tsx';
import { MachineTeamsView } from './MachineTeamsView.tsx';
import { MemoryView } from './MemoryView.tsx';
import { OfficeView } from './OfficeView.tsx';
import { OperationsView } from './OperationsView.tsx';
import { OverviewView } from './OverviewView.tsx';
import { PipelinesView } from './PipelinesView.tsx';
import { PlansView } from './PlansView.tsx';
import { ProjectsView } from './ProjectsView.tsx';
import { ResearchView } from './ResearchView.tsx';
import { RoutinesView } from './RoutinesView.tsx';
import { ScreenView } from './ScreenView.tsx';
import { SessionsView } from './SessionsView.tsx';
import { SettingsView } from './SettingsView.tsx';
import { TasksView } from './TasksView.tsx';
import { TeamWorkView } from './TeamWorkView.tsx';
import { TerminalView } from './TerminalView.tsx';

export type ViewId =
  | 'overview'
  | 'sessions'
  | 'terminal'
  | 'console'
  | 'apps'
  | 'activity'
  | 'settings'
  | 'research'
  | 'team-work'
  | 'plans'
  | 'tasks'
  | 'projects'
  | 'teams'
  | 'git'
  | 'roadmap'
  | 'office'
  | 'routines'
  | 'pipelines'
  | 'operations'
  | 'memory'
  | 'documents'
  | 'architecture'
  | 'screen';

export interface ViewProps {
  w: Workbench;
  relay: boolean;
  admin: boolean;
  /** The screen is offered here (on AI Cloud, with Teams). */
  screen: boolean;
}

export interface ViewDef {
  title: string;
  /** The navigation's words when they differ from the title ("View screen"). */
  navLabel?: string;
  subtitle: string;
  icon: ReactNode;
  tone: ArtTone;
  /** It talks to the machine: hidden without the relay (AI Cloud's free plan), and it says so while the machine is away. */
  relay: boolean;
  /** It fills its frame and scrolls inside itself. */
  fill?: boolean;
  needs?: WorkbenchNeed[];
  render: (p: ViewProps) => ReactNode;
}

export const VIEWS: Record<ViewId, ViewDef> = {
  overview: { title: 'Overview', subtitle: 'What needs you, what is running, its workspaces and its health.', icon: <House />, tone: 'teal', relay: false, render: (p) => <OverviewView w={p.w} relay={p.relay} admin={p.admin} /> },
  sessions: { title: 'Sessions', subtitle: 'Its chats, research and team work, by the names the desktop uses. Open one to carry on.', icon: <Comments />, tone: 'teal', relay: true, fill: true, render: (p) => <SessionsView w={p.w} /> },
  terminal: { title: 'Terminal', subtitle: 'The terminals its person shares with remote control: watch them, and type when allowed.', icon: <Terminal />, tone: 'teal', relay: true, fill: true, render: (p) => <TerminalView w={p.w} /> },
  console: { title: 'Console', subtitle: 'A shell on this node, opened when you ask for it.', icon: <Terminal />, tone: 'teal', relay: true, fill: true, render: (p) => <ConsoleView w={p.w} /> },
  apps: { title: 'Apps', subtitle: 'The small apps its person made or added in AI Workbench.', icon: <Cubes3 />, tone: 'teal', relay: true, render: (p) => <AppsView w={p.w} /> },
  activity: { title: 'Activity', subtitle: 'What ran on this machine, day by day.', icon: <Clock />, tone: 'blue', relay: false, render: (p) => <ActivityView w={p.w} /> },
  settings: { title: 'Settings', subtitle: 'What it allows, its updates, the policy it follows and its security.', icon: <Gear />, tone: 'slate', relay: false, render: (p) => <SettingsView w={p.w} admin={p.admin} screen={p.screen} relay={p.relay} /> },
  research: { title: 'Research', subtitle: 'Have a specialist, a team or a council look into a question and write it up.', icon: <Magnifier />, tone: 'violet', relay: true, fill: true, needs: ['activity', 'events'], render: (p) => <ResearchView w={p.w} /> },
  'team-work': { title: 'Team Work', subtitle: 'Shape a brief, plan it, and let a team carry it out under an operations manager.', icon: <Comments />, tone: 'blue', relay: true, fill: true, needs: ['activity', 'events', 'status'], render: (p) => <TeamWorkView w={p.w} /> },
  plans: { title: 'Plans', subtitle: 'The plans a team is working through, and how far each has got.', icon: <NodesRight />, tone: 'amber', relay: true, needs: ['status'], render: (p) => <PlansView w={p.w} /> },
  tasks: { title: 'Tasks', subtitle: 'The board every task lives on.', icon: <ListCheck />, tone: 'green', relay: true, fill: true, needs: ['activity', 'status'], render: (p) => <TasksView w={p.w} /> },
  projects: { title: 'Projects', subtitle: 'Every project on this machine, its repositories, and what each is doing right now.', icon: <Folder />, tone: 'blue', relay: true, needs: ['activity', 'status', 'workspaces'], render: (p) => <ProjectsView w={p.w} /> },
  teams: { title: 'Teams', subtitle: 'The teams and specialists this machine holds, as the Teams section names them.', icon: <Persons />, tone: 'violet', relay: false, render: (p) => <MachineTeamsView w={p.w} /> },
  git: { title: 'Git', subtitle: 'Branches, worktrees and the commit graph of the active root.', icon: <BranchesRight />, tone: 'rose', relay: true, render: (p) => <GitView w={p.w} /> },
  roadmap: { title: 'Roadmap', subtitle: 'What has landed, what is landing and what is still to come.', icon: <Route />, tone: 'green', relay: true, render: () => <AppOnlyView blurb="Its timeline over the map of the code" /> },
  office: { title: 'Office', subtitle: 'Who is working, on what, right now.', icon: <Display />, tone: 'teal', relay: true, needs: ['activity'], render: (p) => <OfficeView w={p.w} /> },
  routines: { title: 'Routines', subtitle: 'Saved operations that run on a schedule or on demand, and the daily review.', icon: <Clock />, tone: 'amber', relay: true, render: (p) => <RoutinesView w={p.w} /> },
  pipelines: { title: 'Pipelines', subtitle: 'GitHub Actions and Azure Pipelines for the active root: watch, run, re-run.', icon: <Rocket />, tone: 'teal', relay: true, render: (p) => <PipelinesView w={p.w} /> },
  operations: { title: 'Operations', subtitle: 'Agents, spend and the local model: the live state of this machine.', icon: <Pulse />, tone: 'blue', relay: true, needs: ['activity'], render: (p) => <OperationsView w={p.w} /> },
  memory: { title: 'Memory', subtitle: 'The shared knowledge graph agents read from and write back to.', icon: <GraphNode />, tone: 'violet', relay: true, render: (p) => <MemoryView w={p.w} /> },
  documents: { title: 'Documents', subtitle: 'The documents in this repository, and what has gone stale.', icon: <Book />, tone: 'green', relay: true, render: () => <AppOnlyView blurb="Guides, decisions, plans, agent instructions and templates, and what has gone stale" /> },
  architecture: { title: 'Architecture', subtitle: 'A map of this repository: its areas and layers.', icon: <LayoutCells />, tone: 'rose', relay: true, render: () => <AppOnlyView blurb="Its areas and layers, and what depends on what" /> },
  screen: { title: 'Screen', navLabel: 'View screen', subtitle: 'The workbench window, watched live, and driven when you take control. Most things have a quicker way in Sessions.', icon: <Eye />, tone: 'blue', relay: true, fill: true, render: (p) => <ScreenView w={p.w} /> },
};

export interface Offer {
  kind: 'workbench' | 'node';
  /** The relay is offered (not AI Cloud's free plan). */
  relay: boolean;
  /** The screen is offered (on AI Cloud, with Teams). */
  screen: boolean;
}

/** The navigation for this machine: groups in order, and the quiet items at the foot. */
export function viewsFor(o: Offer): { groups: Array<{ label: string; views: ViewId[] }>; quiet: ViewId[] } {
  const keep = (vs: ViewId[]) => vs.filter((v) => o.relay || !VIEWS[v].relay);
  if (o.kind === 'node') {
    const groups = [
      { label: 'This machine', views: keep(['overview', 'sessions', 'console', 'activity', 'settings']) },
      { label: 'Project', views: keep(['teams']) },
      { label: 'Automation', views: keep(['operations']) },
    ];
    return { groups: groups.filter((g) => g.views.length), quiet: [] };
  }
  const groups = [
    { label: 'This machine', views: keep(['overview', 'sessions', 'terminal', 'apps', 'activity', 'settings']) },
    { label: 'Work', views: keep(['research', 'team-work', 'plans', 'tasks']) },
    { label: 'Project', views: keep(['projects', 'teams', 'git', 'roadmap', 'office']) },
    { label: 'Automation', views: keep(['routines', 'pipelines', 'operations']) },
    { label: 'Knowledge', views: keep(['memory', 'documents', 'architecture']) },
  ];
  // Activity is part of Teams on AI Cloud, as the Activity page is.
  const cloudFree = !o.relay;
  return { groups: groups.map((g) => ({ ...g, views: cloudFree ? g.views.filter((v) => v !== 'activity') : g.views })).filter((g) => g.views.length), quiet: o.relay && o.screen ? ['screen'] : [] };
}

/** Older addresses, and the app's own names for views, as the view they now are. */
const ALIASES: Record<string, ViewId> = { chat: 'sessions', live: 'sessions', terminals: 'terminal', shell: 'console', workspace: 'office', facilitator: 'team-work', docs: 'documents', details: 'settings' };

/** The view `?view=` names, if this machine offers it; else Overview. */
export function resolveView(asked: string | null, o: Offer): ViewId {
  const v = (asked && (ALIASES[asked] ?? asked)) as ViewId | null;
  const nav = viewsFor(o);
  const offered = new Set<ViewId>([...nav.groups.flatMap((g) => g.views), ...nav.quiet]);
  if (v && offered.has(v)) return v;
  // A workbench's Terminal and a node's Console are the same place on each.
  if (v === 'terminal' && offered.has('console')) return 'console';
  if (v === 'console' && offered.has('terminal')) return 'terminal';
  return 'overview';
}
